import { OtsDateTime } from '../../components/OtsDateTime';
import { useId, type ReactNode } from 'react';
import type { RrcEp9WasteProfile } from '@ots-frontend/ots-data';
import { AdminPanel } from '../../components/AdminPanel';
import {
  DescriptionList,
  DescriptionItem,
} from '../../components/DescriptionList';
import { OtsActionButton } from '../../components/OtsButton';
import { ComplianceStatusBadge } from '../ComplianceStatusBadge';
import { ep9WasteCodeLabel } from './rrc-ep9-waste-profile-ui';
export type RrcEp9WasteProfileDetailsProps = {
  profile: RrcEp9WasteProfile;
  compact?: boolean;
  actions?: ReactNode;
  onOpenCompanyProfile?: (id: string) => void;
  onOpenLocationProfile?: (id: string) => void;
};
export const RrcEp9WasteProfileDetails = ({
  profile: p,
  compact,
  actions,
  onOpenCompanyProfile,
  onOpenLocationProfile,
}: RrcEp9WasteProfileDetailsProps) => {
  const headingId = useId();
  return (
    <div className="@container space-y-4">
      <AdminPanel
        ariaLabelledBy={headingId}
        title={`Waste ${p.wasteId}`}
        description={`Version ${p.version} · ${ep9WasteCodeLabel(p.wasteType)}`}
        compact={compact}
        bodyClassName="@container p-4"
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ComplianceStatusBadge status={p.status} compact={compact} />
            {actions}
          </div>
        }
      >
        <DescriptionList compact={compact} responsive="container">
          <DescriptionItem label="Subject">
            {p.objectName ?? p.objectId}
          </DescriptionItem>
          <DescriptionItem label="Subject type">{p.objectType}</DescriptionItem>
          <DescriptionItem label="Effective start">
            <OtsDateTime value={p.effectiveStartDttm} empty="Not set" />
          </DescriptionItem>
          <DescriptionItem label="Effective end">
            <OtsDateTime value={p.effectiveEndDttm} empty="No end date" />
          </DescriptionItem>
          <DescriptionItem label="Approved by">
            {p.approvedByName ?? p.approvedById}
          </DescriptionItem>
          <DescriptionItem label="Approved at">
            <OtsDateTime value={p.approvedDttm} empty="Not approved" />
          </DescriptionItem>
        </DescriptionList>
      </AdminPanel>
      <AdminPanel
        title="Waste characterization"
        compact={compact}
        bodyClassName="@container p-4"
      >
        <DescriptionList compact={compact} responsive="container">
          <DescriptionItem label="Physical characteristics">
            {ep9WasteCodeLabel(p.wastePhysicalCharacteristics)}
          </DescriptionItem>
          <DescriptionItem label="Classification">
            {ep9WasteCodeLabel(p.wasteClassification)}
          </DescriptionItem>
          <DescriptionItem label="Basis">
            {ep9WasteCodeLabel(p.wasteBasis)}
          </DescriptionItem>
          <DescriptionItem label="NORM reading (µR/hr)">
            {p.wasteNormReading ?? 'Not specified'}
          </DescriptionItem>
          <DescriptionItem label="RCRA exempt">
            {p.rcraExempt == null
              ? 'Not specified'
              : p.rcraExempt
              ? 'Yes'
              : 'No'}
          </DescriptionItem>
          <DescriptionItem label="Chemical characteristics">
            <span className="whitespace-pre-wrap">
              {p.wasteChemicalCharacteristics}
            </span>
          </DescriptionItem>
          <DescriptionItem label="Constituents">
            <span className="whitespace-pre-wrap">{p.wasteConstituents}</span>
          </DescriptionItem>
        </DescriptionList>
      </AdminPanel>
      <AdminPanel
        title="Generator snapshot"
        compact={compact}
        bodyClassName="@container p-4"
        actions={
          <div className="flex flex-wrap gap-2">
            {onOpenCompanyProfile && p.rrcCompanyProfileId && (
              <OtsActionButton
                size="compact"
                onClick={() => onOpenCompanyProfile(p.rrcCompanyProfileId)}
              >
                Open company profile
              </OtsActionButton>
            )}
            {onOpenLocationProfile && p.rrcLocationProfileId && (
              <OtsActionButton
                size="compact"
                onClick={() => onOpenLocationProfile(p.rrcLocationProfileId!)}
              >
                Open location profile
              </OtsActionButton>
            )}
          </div>
        }
      >
        <DescriptionList compact={compact} responsive="container">
          <DescriptionItem label="Organization">
            {p.generatorOrgName}
          </DescriptionItem>
          <DescriptionItem label="P5 number">
            {p.generatorP5Number}
          </DescriptionItem>
          <DescriptionItem label="Commercial">
            {p.generatorCommercial ? 'Yes' : 'No'}
          </DescriptionItem>
          <DescriptionItem label="Facility">
            {p.generatorLocationFacilityName}
          </DescriptionItem>
          <DescriptionItem label="Lease name">
            {p.generatorLocationLeaseName}
          </DescriptionItem>
          <DescriptionItem label="Lease number">
            {p.generatorLocationLeaseNumber}
          </DescriptionItem>
          <DescriptionItem label="Gas plant number">
            {p.generatorLocationGasPlantNumber}
          </DescriptionItem>
          <DescriptionItem label="API number">
            {p.generatorLocationApiNumber}
          </DescriptionItem>
          <DescriptionItem label="Other identifier">
            {p.generatorLocationOtherIdentifier}
          </DescriptionItem>
          <DescriptionItem label="County">
            {p.generatorLocationCounty}
          </DescriptionItem>
          <DescriptionItem label="Coordinates">
            {p.generatorLocationLatCoord != null &&
            p.generatorLocationLongCoord != null
              ? `${p.generatorLocationLatCoord}, ${p.generatorLocationLongCoord}`
              : 'Not specified'}
          </DescriptionItem>
          <DescriptionItem label="Contact">{p.contactName}</DescriptionItem>
          <DescriptionItem label="Phone">{p.contactPhone}</DescriptionItem>
          <DescriptionItem label="Email">{p.contactEmail}</DescriptionItem>
        </DescriptionList>
      </AdminPanel>
      <AdminPanel
        title="Record history"
        compact={compact}
        bodyClassName="@container p-4"
      >
        <DescriptionList compact={compact} responsive="container">
          <DescriptionItem label="Created">
            <OtsDateTime value={p.createDttm} empty="—" />
          </DescriptionItem>
          <DescriptionItem label="Created by">
            {p.createdByName}
          </DescriptionItem>
          <DescriptionItem label="Updated">
            <OtsDateTime value={p.lastModifiedDttm} empty="—" />
          </DescriptionItem>
          <DescriptionItem label="Updated by">
            {p.lastModifiedByName}
          </DescriptionItem>
        </DescriptionList>
      </AdminPanel>
    </div>
  );
};
